import * as api from '@apis/admin-user-contents';
import * as feedApi from '@apis/app-feed';
import DateTimeButtonPicker from '@components/class-invoice/DateTimeButtonPicker';
import { SelectUser } from '@components/product/SelectUser';
import { UserContentDetailCard } from '@components/user-contents/UserContentDetailCard';
import UserContentSummary from '@components/user-contents/UserContentSummary';
import { useAuthentication } from '@hooks/authentication';
import { RefundStatus } from '@models/paymentStatement';
import { ExtendedUserContent, UserContent, UserContentStatus } from '@models/user-contents';
import { useLoungeEditorModalStore } from '@stores/loungeEditorModalStore';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { useUserContentsStore } from '@stores/userContentsStore';
import { useMutation, useQuery } from '@tanstack/react-query';
import { localTimeToUtcISOString, utcTimeToLocalISOString } from '@utils/date';
import { PRODUCT_SHARE_BASE_URL } from '@variables';
import { Button, DatePicker, DatePickerProps, Input, Modal, Select, Table, Tooltip } from 'antd';
import { FilterSelect, filterActiveClassName } from '@components/common/FilterSelect';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import moment from 'moment';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ExtendedUserContentComment } from '@models/user-content-comment';

const USER_CONTENTS_STATUS_LABELS: { [key: string]: string } = {
  [UserContentStatus.OPEN]: '게시중',
  [UserContentStatus.PENDING]: '대기',
  [UserContentStatus.SUGGESTION]: '제안',
  [UserContentStatus.HIDDEN]: '숨김',
  [UserContentStatus.RESERVED]: '예약',
  [UserContentStatus.END]: '예약 종료',
  [UserContentStatus.DELIST]: '삭제대기',
};

export const UserContentsPage: React.FC = () => {
  const loungeEditorModal = useLoungeEditorModalStore();
  const [reserveDate, setReserveDate] = useState<{ startDate?: Date; endDate?: Date }>({});
  const [changeReserveContentId, setChangeReserveContentId] = useState<string>();
  const [changePublisherContentId, setChangePublisherContentId] = useState<string>();
  const [selectedUserId, setSelectedUserId] = useState<string>();
  const [searchKeyword, setSearchKeyword] = useState<string>();
  const [showContent, setShowContent] = useState<any>();
  const searchInputRef = useRef<any>();
  const dialog = useOkCancelDialog();

  const store = useUserContentsStore();

  useAuthentication();

  const onChangeStartDate: DatePickerProps['onChange'] = (date) => {
    store.setStartDate(localTimeToUtcISOString(date ? new Date(date.toISOString()) : new Date(), { timeToZero: true }));
  };

  const onChangeEndDate: DatePickerProps['onChange'] = (date) => {
    store.setEndDate(localTimeToUtcISOString(date ? new Date(date.toISOString()) : new Date(), { timeToZero: true }));
  };

  const userContentsQuery = useQuery({
    queryKey: [`userContents`, JSON.stringify(store.searchQuery)],
    queryFn: () => api.searchUserContents(store.searchQuery),
  });

  const loungeCurationCategoriesQuery = useQuery({
    queryKey: [`lounge-curation-categories`],
    queryFn: () => feedApi.getLoungeCurationCategories(),
  });
  const loungeCurationCategories = loungeCurationCategoriesQuery.data?.data?.data || [];
  const loungeCurationCategoriesDict: { [key: string]: string | undefined } = {};
  loungeCurationCategories.forEach((item) => {
    loungeCurationCategoriesDict[item.type] = item.title;
  });

  const updateUserContentStatusMutation = useMutation({
    mutationFn: api.updateUserContentStatus,
    onSuccess: async () => {
      toast.dark('콘텐츠 상태를 변경했습니다', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        pauseOnFocusLoss: false,
        draggable: true,
        progress: undefined,
        bodyStyle: {
          color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
          textAlign: 'center',
          fontFamily: 'Pretendard',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '20px',
        },
        theme: 'dark',
      });
      userContentsQuery.refetch();
    },
  });

  const deleteUserContentMutation = useMutation({
    mutationFn: api.deleteUserContent,
    onSuccess: async () => {
      userContentsQuery.refetch();
    },
  });

  const updateUserContentMutation = useMutation({
    mutationFn: api.updateUserContent,
    onSuccess: async () => {
      setChangePublisherContentId(undefined);
      setSelectedUserId(undefined);
      setChangeReserveContentId(undefined);
      setReserveDate({});
      toast.dark('콘텐츠를 수정했습니다', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        pauseOnFocusLoss: false,
        draggable: true,
        progress: undefined,
        bodyStyle: {
          color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
          textAlign: 'center',
          fontFamily: 'Pretendard',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '20px',
        },
        theme: 'dark',
      });
      userContentsQuery.refetch();
    },
  });

  const userContents = userContentsQuery.data?.data;

  useEffect(() => {
    if (userContents) {
      store.setUserContentsContainer(userContents);
    }
  }, [userContents]);

  useEffect(() => {
    userContentsQuery.refetch();
  }, [loungeEditorModal.show]);

  const TABLE_COLUMNS: ColumnsType<UserContent> = [
    {
      title: 'ID',
      align: 'center',
      render: (record: UserContent) => {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(record._id);
                toast.dark('ID를 클립보드에 복사했습니다', {
                  position: 'top-center',
                  autoClose: 3000,
                  hideProgressBar: true,
                  closeOnClick: true,
                  pauseOnHover: true,
                  pauseOnFocusLoss: false,
                  draggable: true,
                  progress: undefined,
                  bodyStyle: {
                    color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                    textAlign: 'center',
                    fontFamily: 'Pretendard',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '20px',
                  },
                  theme: 'dark',
                });
              }}>
              콘텐츠 ID
            </Button>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(record.paymentStatementId || '');
                toast.dark('결제내역 ID를 클립보드에 복사했습니다', {
                  position: 'top-center',
                  autoClose: 3000,
                  hideProgressBar: true,
                  closeOnClick: true,
                  pauseOnHover: true,
                  pauseOnFocusLoss: false,
                  draggable: true,
                  progress: undefined,
                  bodyStyle: {
                    color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                    textAlign: 'center',
                    fontFamily: 'Pretendard',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '20px',
                  },
                  theme: 'dark',
                });
              }}>
              결제 ID
            </Button>
          </div>
        );
      },
    },
    {
      title: '구분',
      align: 'center',
      render: (record) => {
        let board = record.board;
        switch (record.board) {
          case 'review':
            board = '후기';
            break;
          case 'curation':
            board = '콘텐츠발견';
            break;
          case 'partner-news':
            board = '선생님소식';
            break;
          case 'vote':
            board = '투표';
            break;
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div>{board}</div>
            {record.bestDate && (
              <div
                style={{
                  width: '52px',
                  height: '24px',
                  lineHeight: '24px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#ffffff',
                  textAlign: 'center',
                  background: 'orange',
                }}>
                베스트
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '상태',
      align: 'center',
      render: (record) => {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
            <Select
              style={{ width: '100px' }}
              value={record.status}
              options={Object.keys(USER_CONTENTS_STATUS_LABELS).map((key) => {
                return { value: key, label: USER_CONTENTS_STATUS_LABELS[key] };
              })}
              onChange={(value) => {
                updateUserContentStatusMutation.mutate({
                  userContentId: record._id,
                  status: value,
                });
              }}></Select>
            {record.status === UserContentStatus.RESERVED && (
              <button
                className="link-button"
                style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '8px 12px' }}
                onClick={() => {
                  setChangeReserveContentId(record._id);
                  setReserveDate({
                    startDate: record.startDate ? new Date(record.startDate) : undefined,
                    endDate: record.endDate ? new Date(record.endDate) : undefined,
                  });
                }}>
                <div>
                  {record.startDate && <div>{moment(record.startDate).format('YYYY-MM-DD HH:mm')}</div>}
                  <div>~</div>
                  {record.endDate && <div>{moment(record.endDate).format('YYYY-MM-DD HH:mm')}</div>}
                </div>
              </button>
            )}
            {record.status === UserContentStatus.OPEN && record.endDate && (
              <button
                className="link-button"
                style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '8px 12px' }}
                onClick={() => {
                  setChangeReserveContentId(record._id);
                  setReserveDate({
                    startDate: record.startDate ? new Date(record.startDate) : undefined,
                    endDate: record.endDate ? new Date(record.endDate) : undefined,
                  });
                }}>
                <div>
                  <div>~</div>
                  {record.endDate && <div>{moment(record.endDate).format('YYYY-MM-DD HH:mm')}</div>}
                </div>
              </button>
            )}
          </div>
        );
      },
    },
    {
      title: '판매자',
      align: 'center',
      width: 100,
      render: (record) => {
        const sellerName = record?.paymentStatement?.seller?.name ?? '-';
        return <div>{sellerName}</div>;
      },
    },
    {
      title: '상품',
      align: 'center',
      width: 150,
      render: (record) => {
        const productTitle = record?.product?.title ?? '-';
        return <div>{productTitle}</div>;
      },
    },
    {
      title: '작성자',
      align: 'center',
      render: (record) => {
        const publisher = record.publisher;
        const editor = record.editor;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
            <div>{`${publisher?.profile?.name}\n(${publisher?.profile?.phone?.substring(
              publisher?.profile?.phone?.length - 4,
            )})`}</div>
            {editor?.profile?.name && (
              <Tooltip
                title={`${editor?.profile?.name}\n(${editor?.profile?.phone?.substring(
                  editor?.profile?.phone?.length - 4,
                )})`}></Tooltip>
            )}
            <Button
              onClick={() => {
                setChangePublisherContentId(record._id);
              }}>
              변경
            </Button>
          </div>
        );
      },
    },
    {
      title: '마케팅 동의',
      align: 'center',
      width: 100,
      render: (record) => {
        return <div>{record.marketingAgreed ? '동의' : '미동의'}</div>;
      },
    },
    {
      title: '내용',
      width: 480,
      render: (record) => {
        return (
          <div style={{ width: '100%' }}>
            <UserContentSummary
              item={record}
              loungeCurationCategoriesDict={loungeCurationCategoriesDict}></UserContentSummary>
          </div>
        );
      },
    },
    {
      title: '동작',
      align: 'center',
      render: (record: ExtendedUserContent) => {
        const userContent = record;
        const shareUrl = `${PRODUCT_SHARE_BASE_URL}/uc/${userContent?._id}`;

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(shareUrl);
                toast.dark('공유링크를 클립보드에 복사했습니다', {
                  position: 'top-center',
                  autoClose: 3000,
                  hideProgressBar: true,
                  closeOnClick: true,
                  pauseOnHover: true,
                  pauseOnFocusLoss: false,
                  draggable: true,
                  progress: undefined,
                  bodyStyle: {
                    color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                    textAlign: 'center',
                    fontFamily: 'Pretendard',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '20px',
                  },
                  theme: 'dark',
                });
              }}>
              공유링크 복사
            </Button>
            <Button
              onClick={() => {
                setShowContent(<UserContentDetailCard userContent={userContent} />);
              }}>
              보기
            </Button>
            <Button
              onClick={() => {
                loungeEditorModal.open({
                  userContentId: record._id,
                  paymentStatement: record?.paymentStatement,
                  board: record.board,
                });
              }}>
              편집
            </Button>
            <Button
              danger
              onClick={() => {
                dialog.open({
                  title: '삭제 확인',
                  content: '삭제하시겠습니까?',
                  type: 'ok_cancel',
                  onConfirm: () => {
                    deleteUserContentMutation.mutate(record._id);
                  },
                });
              }}>
              삭제
            </Button>
            <Button
              type="primary"
              onClick={async () => {
                const commentsResponse = await api.getComments(record._id);
                const comments = commentsResponse.data;

                setShowContent(
                  <div style={{ padding: '20px', maxHeight: '80vh', overflowY: 'auto' }}>
                    <h3 style={{ marginBottom: '20px' }}>댓글 관리 ({comments.length}개)</h3>
                    {comments.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#9B9DA0' }}>댓글이 없습니다.</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {comments.map((comment: ExtendedUserContentComment) => (
                          <div
                            key={comment._id}
                            style={{
                              marginLeft: comment.depth * 16,
                              padding: '16px',
                              border: '1px solid #EFF1F4',
                              borderRadius: '8px',
                              backgroundColor: comment.status === 'deleted' ? '#F5F7FB' : 'white',
                            }}>
                            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    gap: '8px',
                                    marginBottom: '8px',
                                  }}>
                                  <div style={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                                    <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                      {comment.editor?.profile?.nickname || '탈퇴한 사용자'}
                                    </span>
                                    <span>
                                      {comment.editor?.profile?.name}({comment.editor?.profile?.phone || ''})
                                    </span>
                                  </div>
                                  <span style={{ fontSize: '12px', color: '#9B9DA0' }}>
                                    {moment(comment.createdAt).format('YYYY-MM-DD HH:mm')}
                                  </span>
                                  {comment.depth > 0 && (
                                    <span
                                      style={{
                                        fontSize: '11px',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        backgroundColor: '#EBF9FF',
                                        color: '#1890FF',
                                      }}>
                                      대댓글
                                    </span>
                                  )}
                                  {comment.status === 'deleted' && (
                                    <span
                                      style={{
                                        fontSize: '11px',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        backgroundColor: '#FFF1F0',
                                        color: '#FF4D4F',
                                      }}>
                                      삭제됨
                                    </span>
                                  )}
                                  {comment.status === 'hidden' && (
                                    <span
                                      style={{
                                        fontSize: '11px',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        backgroundColor: '#FFF7E6',
                                        color: '#FA8C16',
                                      }}>
                                      숨김
                                    </span>
                                  )}
                                </div>
                                <div
                                  style={{
                                    whiteSpace: 'pre-wrap',
                                    marginBottom: '8px',
                                    borderTop: '1px solid #EFF1F4',
                                    paddingTop: '8px',
                                  }}>
                                  {comment.content}
                                </div>
                                {comment.reports && comment.reports.length > 0 && (
                                  <div
                                    style={{
                                      marginTop: '12px',
                                      padding: '12px',
                                      backgroundColor: '#FFF1F0',
                                      borderRadius: '4px',
                                    }}>
                                    <div
                                      style={{
                                        fontWeight: 600,
                                        color: '#FF4D4F',
                                        marginBottom: '8px',
                                      }}>
                                      신고 내역 ({comment.reports.length}건)
                                    </div>
                                    {comment.reports.map((report: any, idx: number) => (
                                      <div
                                        key={idx}
                                        style={{
                                          fontSize: '12px',
                                          padding: '8px',
                                          backgroundColor: 'white',
                                          borderRadius: '4px',
                                          marginBottom: idx < (comment.reports?.length || 0) - 1 ? '8px' : 0,
                                        }}>
                                        <div style={{ marginBottom: '4px' }}>
                                          <strong>신고자:</strong> {report.reporter?.profile?.name}(
                                          {report.reporter?.id})
                                        </div>
                                        <div style={{ marginBottom: '4px' }}>
                                          <strong>사유:</strong> {report.reason}
                                        </div>
                                        {report.reasonDetail && (
                                          <div style={{ marginBottom: '4px', color: '#666' }}>
                                            상세: {report.reasonDetail}
                                          </div>
                                        )}
                                        <div style={{ color: '#9B9DA0', fontSize: '11px' }}>
                                          {moment(report.createdAt).format('YYYY-MM-DD HH:mm')}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {comment.status === 'active' && (
                                  <>
                                    <Button
                                      size="small"
                                      danger
                                      onClick={async () => {
                                        dialog.open({
                                          title: '댓글 숨기기',
                                          content: '이 댓글을 숨기시겠습니까?',
                                          type: 'ok_cancel',
                                          onConfirm: async () => {
                                            try {
                                              await api.updateCommentStatus(comment._id, 'hidden');
                                              toast.success('댓글이 숨겨졌습니다.');
                                              setShowContent(null);
                                              // 댓글 목록 새로고침
                                              await api.getComments(record._id);
                                            } catch (error) {
                                              toast.error('댓글 숨기기에 실패했습니다.');
                                            }
                                          },
                                        });
                                      }}>
                                      숨기기
                                    </Button>
                                  </>
                                )}
                                {comment.status === 'hidden' && (
                                  <Button
                                    size="small"
                                    type="primary"
                                    onClick={async () => {
                                      dialog.open({
                                        title: '댓글 복구',
                                        content: '이 댓글을 복구하시겠습니까?',
                                        type: 'ok_cancel',
                                        onConfirm: async () => {
                                          try {
                                            await api.updateCommentStatus(comment._id, 'active');
                                            toast.success('댓글이 복구되었습니다.');
                                            setShowContent(null);
                                            // 댓글 목록 새로고침
                                            await api.getComments(record._id);
                                          } catch (error) {
                                            toast.error('댓글 복구에 실패했습니다.');
                                          }
                                        },
                                      });
                                    }}>
                                    복구
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>,
                );
              }}>
              댓글관리
            </Button>
          </div>
        );
      },
    },
    {
      title: '통계',
      render: (record: ExtendedUserContent) => {
        return (
          <div>
            <div>{`조회수: ${record.viewCount}`}</div>
            <div>{`찜수: ${record.favoriteCount}`}</div>
            <div>{`공유수: ${record.shareCount}`}</div>
            {record.board === 'vote' && <div>{`투표수: ${record.voteResult?.summary.total}`}</div>}
            {record.board === 'vote' && (
              <ul style={{ margin: 0, padding: '0 8px' }}>
                {record.voteResult?.results.map((item) => {
                  const option = record.vote?.options.find((option) => option.id === item.optionId);
                  return (
                    <li key={item.optionId} style={{ color: option?.color }}>{`${option?.text}: ${item.count}`}</li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      },
    },
    {
      title: '리뷰 정보',
      width: 240,
      render: (record: UserContent) => {
        if (record.board !== 'review') {
          return '-';
        }

        const paymentStatement = record?.paymentStatement;
        const total =
          (record.rates?.rate1 || 0) +
          (record.rates?.rate2 || 0) +
          (record.rates?.rate3 || 0) +
          (record.rates?.rate4 || 0);
        const latestReviewAvg = Number((total / 4).toFixed(2));

        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Tooltip
              overlay={
                <table>
                  <tr>
                    <td>친화력</td>
                    <td>전문성</td>
                    <td>준비성</td>
                    <td>시간준수</td>
                    <td>아이 만족도</td>
                  </tr>
                  <tr>
                    <td>{`${record.rates?.rate1}`}</td>
                    <td>{`${record.rates?.rate2}`}</td>
                    <td>{`${record.rates?.rate3}`}</td>
                    <td>{`${record.rates?.rate4}`}</td>
                    <td>{`${record.rates?.rate5}`}</td>
                  </tr>
                </table>
              }>
              <div>
                <span>평점: </span>
                <span>{latestReviewAvg}</span>
              </div>
            </Tooltip>
            {paymentStatement && (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <div>결제일시:</div>
                  <div>
                    {paymentStatement.createdAt && moment(paymentStatement.createdAt).format('YYYY.MM.DD HH:mm')}
                  </div>
                </div>
                {(paymentStatement.plannedDates || []).length > 0 && (
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <div>수업일시:</div>
                    <div>
                      {(paymentStatement.plannedDates || []).map((date) => {
                        return <div>{moment(date).format('YYYY.MM.DD HH:mm')}</div>;
                      })}
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '4px' }}>
                  <div>상태:</div>
                  <div>
                    {(() => {
                      const payStatus = (paymentStatement.statuses.payment || '').toLowerCase();
                      let status = '';
                      if (payStatus === 'done') {
                        status = '결제완료';
                      } else if (payStatus === 'ready') {
                        status = '결제대기';
                      } else if (payStatus === 'none') {
                        status = '';
                      } else {
                        status = payStatus;
                      }
                      return status;
                    })()}
                  </div>
                  <div style={{ fontWeight: 'bold' }}>
                    {(() => {
                      const refundStatus = paymentStatement.statuses?.refund;
                      let status = '';
                      if (refundStatus === RefundStatus.ALL) {
                        status = '전체환불';
                      } else if (refundStatus === RefundStatus.PARTIAL) {
                        status = '부분환불';
                      } else if (refundStatus === RefundStatus.ACCEPTED) {
                        status = '환불접수';
                      } else if (refundStatus === RefundStatus.RESERVED) {
                        status = '환불예약';
                      }
                      return status;
                    })()}
                  </div>
                </div>
              </div>
            )}
            <Button
              style={{ color: record.bestDate ? 'red' : 'rgba(0, 0, 0, 0.88)' }}
              onClick={() => {
                if (record.bestDate) {
                  dialog.open({
                    title: '베스트 해제 확인',
                    content: `${record.publisher?.profile?.name}님의 리뷰를 베스트에서 제외합니다.\n베스트에서 제외되더라도 포인트가 회수되지 않습니다.`,
                    type: 'ok_cancel',
                    onConfirm: () => {
                      const formData: FormData = new FormData();
                      formData.append('json', JSON.stringify({ bestDate: record.bestDate ? null : new Date() }));
                      formData.append('userContentId', record._id);
                      updateUserContentMutation.mutate(formData);
                    },
                  });
                } else {
                  if (record.bestPointGrantedAt) {
                    dialog.open({
                      title: '베스트 재선정 확인',
                      content: `${record.publisher?.profile?.name}님의 리뷰를 베스트로 다시 선정합니다.\n포인트 지급 이력이 있어 포인트가 다시 지급되지는 않습니다.`,
                      type: 'ok_cancel',
                      onConfirm: () => {
                        const formData: FormData = new FormData();
                        formData.append('json', JSON.stringify({ bestDate: record.bestDate ? null : new Date() }));
                        formData.append('userContentId', record._id);
                        updateUserContentMutation.mutate(formData);
                      },
                    });
                  } else {
                    dialog.open({
                      title: '베스트 선정 확인',
                      content: `${record.publisher?.profile?.name}님의 리뷰를 베스트로 선정합니다.\n${Math.floor(
                        (paymentStatement?.payment.amount || 0) * 0.05,
                      ).toLocaleString()} 포인트가 지급됩니다.`,
                      type: 'ok_cancel',
                      onConfirm: () => {
                        const formData: FormData = new FormData();
                        formData.append('json', JSON.stringify({ bestDate: record.bestDate ? null : new Date() }));
                        formData.append('userContentId', record._id);
                        updateUserContentMutation.mutate(formData);
                      },
                    });
                  }
                }
              }}>
              {record.bestDate ? '베스트 해제' : '베스트 선정'}
            </Button>
          </div>
        );
      },
    },
    {
      title: '시간',
      width: 180,
      align: 'center',
      render: (record) => {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '4px' }}>
              <div>생성</div>
              <div>{moment(record.createdAt).format('YYYY.MM.DD HH:mm')}</div>
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <div>수정</div>
              <div>{moment(record.updatedAt).format('YYYY.MM.DD HH:mm')}</div>
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <div>게시</div>
              <div>{record.publishedAt ? moment(record.publishedAt).format('YYYY.MM.DD HH:mm') : '-'}</div>
            </div>
            {record.bestDate && (
              <div style={{ display: 'flex', gap: '4px' }}>
                <div>B선정</div>
                <div>{record.bestDate ? moment(record.bestDate).format('YYYY.MM.DD HH:mm') : '-'}</div>
              </div>
            )}
            {record.bestPointGrantedAt && (
              <div style={{ display: 'flex', gap: '4px' }}>
                <div>B지급</div>
                <div>
                  {record.bestPointGrantedAt ? moment(record.bestPointGrantedAt).format('YYYY.MM.DD HH:mm') : '-'}
                </div>
              </div>
            )}
          </div>
        );
      },
    },
  ];
  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      <Modal
        width={600}
        open={!!changePublisherContentId}
        onCancel={() => {
          setChangePublisherContentId(undefined);
        }}
        footer={
          <div>
            <Button
              onClick={() => {
                setChangePublisherContentId(undefined);
              }}>
              취소
            </Button>
            <Button
              style={{ color: 'red' }}
              onClick={() => {
                if (!selectedUserId) {
                  dialog.open({ type: 'ok', title: '알림', content: '유저를 선택해주세요' });
                  return;
                }

                const formData: FormData = new FormData();
                formData.append('json', JSON.stringify({ publisherId: selectedUserId }));
                formData.append('userContentId', changePublisherContentId!);
                updateUserContentMutation.mutate(formData);
              }}>
              변경하기
            </Button>
          </div>
        }>
        <div>
          <div style={{ height: '44px' }}>게시자 변경</div>
          <SelectUser
            style={{ width: '528px' }}
            selectedUserId={selectedUserId}
            onSelect={(user) => {
              setSelectedUserId(user._id);
            }}></SelectUser>
        </div>
      </Modal>
      <Modal
        open={!!changeReserveContentId}
        onCancel={() => {
          setChangeReserveContentId(undefined);
        }}
        footer={
          <div>
            <Button
              onClick={() => {
                setChangeReserveContentId(undefined);
              }}>
              취소
            </Button>
            <Button
              style={{ color: 'red' }}
              onClick={() => {
                const formData: FormData = new FormData();
                const dataToSend = {
                  startDate: reserveDate.startDate ?? null,
                  endDate: reserveDate.endDate ?? null,
                };
                formData.append('json', JSON.stringify(dataToSend));
                if (changeReserveContentId) {
                  formData.append('userContentId', changeReserveContentId);
                }

                updateUserContentMutation.mutate(formData);
              }}>
              변경하기
            </Button>
          </div>
        }>
        <div>
          <div style={{ height: '44px' }}>콘텐츠 예약 변경</div>
          <div>
            <div>시작</div>
            <DateTimeButtonPicker
              minDate={moment().format('YYYY-MM-DD')}
              value={reserveDate.startDate}
              onChange={(value) => {
                setReserveDate((prev) => {
                  return { ...prev, startDate: value };
                });
              }}
            />
          </div>
          <div>
            <div style={{ marginTop: '4px' }}>종료</div>
            <DateTimeButtonPicker
              minDate={moment().format('YYYY-MM-DD')}
              value={reserveDate.endDate}
              onChange={(value) => {
                setReserveDate((prev) => {
                  return { ...prev, endDate: value };
                });
              }}
            />
          </div>
        </div>
      </Modal>
      <Modal
        open={!!showContent}
        onOk={() => {
          setShowContent(undefined);
        }}
        onCancel={() => {
          setShowContent(undefined);
        }}>
        <div style={{ height: '36px' }}></div>
        <OverlayScrollbarsComponent
          defer
          options={{
            scrollbars: { autoHide: 'scroll' },
            overflow: {
              x: 'hidden',
            },
          }}
          style={{
            backgroundColor: '#ffffff',
            marginTop: 0,
            position: 'relative',
            overflow: 'auto',
            width: '100%',
            height: 'calc(100dvh - 280px)',
          }}>
          {showContent}
        </OverlayScrollbarsComponent>
      </Modal>
      <div className="page-header">
        <div>라운지 콘텐츠 관리</div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div>
            <DatePicker
              onChange={onChangeStartDate}
              value={dayjs(utcTimeToLocalISOString(store.searchQuery.startDate as any))}
            />
          </div>
          <div>
            <DatePicker
              onChange={onChangeEndDate}
              value={dayjs(utcTimeToLocalISOString(store.searchQuery.endDate as any))}
            />
          </div>
        </div>
      </div>
      <div style={{ display: 'flex' }}>
        <div className="page-content" style={{ width: '100%', height: 'calc(100vh - 80px)', overflow: 'auto' }}>
          <div
            style={{
              display: 'flex',
              gap: '12px',
              width: '100%',
              height: '52px',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <FilterSelect
                inactiveValue="all"
                value={store.searchQuery.board || 'all'}
                style={{ width: 180 }}
                onChange={(value) => {
                  store.updateSearchQuery({ board: value });
                }}
                options={[
                  { value: 'all', label: '구분 전체' },
                  { value: 'curation', label: '콘텐츠' },
                  { value: 'review', label: '리뷰' },
                  { value: 'review_best', label: '베스트 리뷰' },
                  { value: 'partner-news', label: '선생님 소식' },
                  { value: 'vote', label: '투표' },
                ]}
              />
              <FilterSelect
                inactiveValue="createdAt"
                value={store.searchQuery.sort || 'createdAt'}
                style={{ width: 180 }}
                onChange={(value) => {
                  store.updateSearchQuery({ sort: value });
                }}
                options={[
                  { value: 'createdAt', label: '최근 작성 순' },
                  { value: 'publishedAt', label: '최근 게시 순' },
                  { value: 'bestDate', label: '베스트 선정 순' },
                ]}
              />
              <Input
                className={filterActiveClassName(!!store.searchQuery.keyword)}
                ref={searchInputRef}
                style={{ width: '240px' }}
                value={searchKeyword}
                placeholder={'검색어 입력 후 엔터'}
                onChange={(e) => {
                  setSearchKeyword(e.target.value);
                }}
                onKeyUp={(e) => {
                  if (e.code === 'Enter' && searchKeyword) {
                    store.updateSearchQuery({ keyword: searchKeyword });
                  }
                }}></Input>
              <Button
                onClick={() => {
                  setSearchKeyword(undefined);
                  store.resetSearchQuery();
                }}>
                검색 초기화
              </Button>
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Button
                style={{
                  width: 120,
                  marginLeft: '8px',
                }}
                onClick={() => {
                  loungeEditorModal.open(undefined);
                }}>
                {'새글 작성하기'}
              </Button>
              <Button
                onClick={async () => {
                  const fileName = `igogo-user-contents-${moment().format('YYYYMMDD-HHmmss')}.xlsx`;
                  const blob = await api.getUserContentsExcelFile({ ...store.searchQuery, board: 'review' });
                  if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
                    // IE variant
                    (window.navigator as any).msSaveOrOpenBlob(new Blob(blob.data), fileName);
                  } else {
                    const url = window.URL.createObjectURL(blob.data);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', fileName);
                    document.body.appendChild(link);
                    link.click();
                    link.parentNode && link.parentNode.removeChild(link);
                  }
                }}>
                엑셀 파일 다운로드(후기)
              </Button>
              <Button
                onClick={async () => {
                  const fileName = `igogo-user-contents-${moment().format('YYYYMMDD-HHmmss')}.xlsx`;
                  const blob = await api.getUserContentsExcelFile({ ...store.searchQuery, board: 'curation' });
                  if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
                    // IE variant
                    (window.navigator as any).msSaveOrOpenBlob(new Blob(blob.data), fileName);
                  } else {
                    const url = window.URL.createObjectURL(blob.data);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', fileName);
                    document.body.appendChild(link);
                    link.click();
                    link.parentNode && link.parentNode.removeChild(link);
                  }
                }}>
                엑셀 파일 다운로드(콘텐츠)
              </Button>
            </div>
          </div>
          <Table
            columns={TABLE_COLUMNS}
            scroll={{ x: 'max-content', y: 'calc(100dvh - 280px)' }}
            style={{ whiteSpace: 'pre-wrap' }}
            rowKey={(record) => record?._id}
            dataSource={store.searchResultContainer.contents.filter((userContent) => userContent?._id)}
            pagination={{
              current: (store.searchResultContainer.page || 0) + 1,
              pageSize: store.searchResultContainer.size || 20,
              total: store.searchResultContainer.total,
            }}
            loading={userContentsQuery.isLoading}
            onChange={(e) => {
              store.updateSearchQuery({ page: (e.current || 2) - 1, size: e.pageSize || 20 });
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default UserContentsPage;
